import { FlexGrid, FlexGridItem } from 'baseui/flex-grid'
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE } from 'baseui/modal'
import { Checkbox } from 'baseui/checkbox'
import { Input, SIZE as INPUT_SIZE } from 'baseui/input'
import { Search, Delete } from 'baseui/icon'
import { Card, StyledBody } from 'baseui/card'
import SDPaginator from '@/shared/components/SDComponents/SDPaginator/SDPaginator'
import useAdditionalProductsTools from '../DevToolsNew/useAdditionalProductsTools'
import AdditionalProductsList from './AdditionalProductsList'
import SDSelect from '@/shared/components/SDComponents/SDSelect/SDSelect'
import { FormControl } from 'baseui/form-control'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import ModalResources from './ModalResources/ModalResources'
import { useTranslation } from 'react-i18next'
import { KIND } from 'baseui/button'
import { useNavigate } from 'react-router'
import { HeaderActions } from './styled-components'

const AdditionalProductsTools = () => {
  const { t } = useTranslation()
  const {
    search,
    setSearch,
    page,
    setPage,
    sortKey,
    sortDirection,
    handleSort,
    showOnlyDeleted,
    showOnlyActive,
    toggleShowOnlyDeleted,
    toggleShowOnlyActive,
    confirmAction,
    closeModal,
    confirmActionAndExecute,
    openDeleteModal,
    openRestoreModal,
    actionLoadingId,
    isLoading,
    error,
    debouncedSearch,
    paginated,
    filteredTotal,
    numPages,
    distinctProductTypes,
    selectedProductType,
    setProductType,
    isOpen,
    close,
    setIsOpen,
    resources,
    updateResources,
    saveResources,
    isSavingResources,
    nettingProduct,
    isOptInSmsHidden,
  } = useAdditionalProductsTools()
  const navigate = useNavigate()

  return (
    <div data-testid='additional-products-tools' style={{ padding: '1.5rem', minHeight: '100vh' }}>
      {/* Header Card */}
      <Card
        overrides={{
          Root: {
            style: { marginBottom: '1.5rem', borderRadius: '0.5rem', boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.08)' },
          },
        }}
      >
        <StyledBody>
          <FlexGrid flexGridColumnCount={2} flexGridColumnGap='scale800' flexGridRowGap='scale400'>
            <FlexGridItem>
              <FormControl label={t('additionalProductsManagement:choose_product_type')}>
                <SDSelect
                  dataTestid='product-type-selector'
                  options={distinctProductTypes}
                  value={selectedProductType || []}
                  onChange={params => setProductType(params?.value || null)}
                  size={INPUT_SIZE.compact}
                />
              </FormControl>
            </FlexGridItem>
            {selectedProductType?.length > 0 && (
              <FlexGridItem>
                <HeaderActions>
                  <SDButton
                    hidden={!nettingProduct}
                    onClick={() => navigate(`/additional-sales/${nettingProduct?.integration_technical_name}/netting`)}
                    kind={KIND.secondary}
                  >
                    {t('additionalProductsManagement:netting')}
                  </SDButton>
                  <SDButton onClick={() => setIsOpen(true)} kind={KIND.primary}>
                    {t('additionalProductsManagement:edit_resources')}
                  </SDButton>
                </HeaderActions>
                <ModalResources
                  isOpen={isOpen}
                  close={close}
                  selectedProductType={selectedProductType}
                  resources={resources[selectedProductType[0]?.value] || []}
                  updateResources={updateResources}
                  saveResources={saveResources}
                  isSavingResources={isSavingResources}
                  isOptInSmsHidden={isOptInSmsHidden}
                />
              </FlexGridItem>
            )}
          </FlexGrid>
        </StyledBody>
      </Card>

      {selectedProductType?.length > 0 && (
        <>
          {/* Filters Card */}
          <Card
            overrides={{
              Root: {
                style: {
                  marginBottom: '1.5rem',
                  borderRadius: '0.5rem',
                  boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.08)',
                },
              },
            }}
          >
            <StyledBody>
              <FlexGrid flexGridColumnCount={1} flexGridRowGap='scale600'>
                {/* Search Bar */}
                <FlexGridItem>
                  <Input
                    id='filter-input'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder={t('additionalProductsManagement:filter_placeholder')}
                    size={INPUT_SIZE.compact}
                    startEnhancer={<Search size='1.25rem' />}
                    endEnhancer={
                      search !== '' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {search && debouncedSearch !== search && (
                            <span style={{ fontSize: '0.75rem', opacity: 0.6, whiteSpace: 'nowrap' }}>
                              {t('additionalProductsManagement:filtering')}
                            </span>
                          )}
                          <Delete
                            size='1.125rem'
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSearch('')}
                            id='clear-button'
                          />
                        </div>
                      ) : null
                    }
                    overrides={{
                      Root: { style: { width: '100%' } },
                    }}
                  />
                </FlexGridItem>

                {/* Checkboxes Row */}
                <FlexGridItem>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                    <Checkbox id='showOnlyDeleted' checked={showOnlyDeleted} onChange={toggleShowOnlyDeleted}>
                      {t('additionalProductsManagement:only_deleted')}
                    </Checkbox>
                    <Checkbox id='showOnlyActive' checked={showOnlyActive} onChange={toggleShowOnlyActive}>
                      {t('additionalProductsManagement:only_active')}
                    </Checkbox>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {isLoading && (
                        <span style={{ fontSize: '0.875rem', color: '#666' }}>
                          {t('additionalProductsManagement:loading')}
                        </span>
                      )}
                      {error && (
                        <span style={{ fontSize: '0.875rem', color: '#D44333', fontWeight: 500 }}>{error}</span>
                      )}
                    </div>
                  </div>
                </FlexGridItem>
              </FlexGrid>
            </StyledBody>
          </Card>

          {/* Table Card */}
          <Card
            overrides={{
              Root: {
                style: { borderRadius: '0.5rem', boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.08)', overflow: 'hidden' },
              },
            }}
          >
            <StyledBody style={{ padding: 0 }}>
              <AdditionalProductsList
                items={paginated}
                onSort={handleSort}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onDelete={openDeleteModal}
                onRestore={openRestoreModal}
                loadingId={actionLoadingId}
              />
            </StyledBody>
          </Card>

          {/* Pagination Card */}
          <Card
            overrides={{
              Root: {
                style: { marginTop: '1rem', borderRadius: '0.5rem', boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.08)' },
              },
            }}
          >
            <StyledBody>
              <FlexGrid flexGridColumnCount={2} flexGridColumnGap='scale800'>
                <FlexGridItem display='flex' alignItems='center'>
                  <div style={{ fontSize: '0.875rem', color: '#666' }}>
                    <strong>{t('additionalProductsManagement:total')}</strong>: {filteredTotal} |
                    <strong> {t('additionalProductsManagement:page')}</strong> {page}/{numPages}
                    {debouncedSearch && (
                      <>
                        {' | '}
                        <strong>{t('additionalProductsManagement:filter')}</strong>: "{debouncedSearch}"
                      </>
                    )}
                  </div>
                </FlexGridItem>
                <FlexGridItem display='flex' alignItems='center' justifyContent='flex-end'>
                  <SDPaginator
                    numPages={numPages}
                    currentPage={page}
                    onPageChange={({ nextPage }) => setPage(Math.min(Math.max(nextPage, 1), numPages))}
                  />
                </FlexGridItem>
              </FlexGrid>
            </StyledBody>
          </Card>

          <Modal
            onClose={closeModal}
            closeable
            isOpen={Boolean(confirmAction)}
            animate
            role={ROLE.dialog}
            size='default'
            unstable_ModalBackdropScroll
          >
            <ModalHeader>{t('additionalProductsManagement:modal.confirmation')}</ModalHeader>
            <ModalBody>
              {confirmAction && (
                <p>
                  {confirmAction.type === 'delete'
                    ? t('additionalProductsManagement:modal.delete_message')
                    : t('additionalProductsManagement:modal.restore_message')}{' '}
                  <strong>{confirmAction.product.name}</strong> (#{confirmAction.product.id})?
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <ModalButton kind='tertiary' onClick={closeModal}>
                {t('additionalProductsManagement:modal.cancel')}
              </ModalButton>
              <ModalButton
                onClick={confirmActionAndExecute}
                isLoading={confirmAction && actionLoadingId === confirmAction.product.id}
                kind={confirmAction?.type === 'delete' ? 'secondary' : 'primary'}
              >
                {confirmAction?.type === 'delete'
                  ? t('additionalProductsManagement:actions.delete')
                  : t('additionalProductsManagement:actions.restore')}
              </ModalButton>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  )
}

export default AdditionalProductsTools
